<script setup lang="ts">
// Vendor
import type { DatePickerProps } from "v-calendar/dist/types/src/use/datePicker.js";

type FormDateProps = DatePickerProps & {
  /**
   * If the field should be disabled. Defaults to "undefined"
   */
  disabled?: boolean;
  /**
   * Debounce time in milliseconds. Set to false to disable. Defaults to "undefined"
   */
  debounce?: string | number | false;
};

const { locale: appLocale } = useI18n();
const {
  locale: datePickerLocale,
  debounce,
  disabled = false,
  popover = {
    visibility: "focus",
  },
  ...remainingProps
} = defineProps<
  Partial<
    FormDateProps & {
      test: string;
    }
  >
>();

const field = useFormControl();

if (!field)
  throw new Error(
    "[FormDate]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const _locale = computed(() => datePickerLocale || appLocale.value);

const _name = unref(field.name);

const nativeInput = ref<HTMLInputElement | null>(null);

const inputHasFocus = ref(false);

const _handleModelChange = useDebounceFn(
  (value?: string) => {
    field.setValue(value || undefined, field.validateOnValueUpdate);
  },
  typeof debounce === "string" ? parseInt(`${debounce || 0}`) : debounce || 0,
);

const _setInputFocus = useDebounceFn((hasFocus: boolean) => {
  inputHasFocus.value = hasFocus;
}, 100);

const _inputModel = defineModel<string | undefined>({
  /**
   * Trigger possibly debounced update of vee-validate form value when v-model updates
   */
  set(value) {
    _handleModelChange(value);

    return value;
  },
});

if (field.value.value) {
  _inputModel.value =
    typeof field.value.value === "string"
      ? field.value.value
      : String(field.value.value || "");
}

function handleIconClick() {
  if (nativeInput.value) {
    nativeInput.value.focus();
  }
}

defineExpose({
  field,
});
</script>

<template>
  <ClientOnly>
    <VDatePicker
      v-bind="remainingProps"
      class="form-date"
      v-model="_inputModel"
      :locale="_locale"
      :popover="popover"
    >
      <template #default="{ inputValue, inputEvents }">
        <div
          :class="[
            'form-date__input-wrapper',
            { 'form-control__native-el--has-focus': inputHasFocus },
          ]"
        >
          <button
            class="form-date__button"
            type="button"
            @click="handleIconClick"
          >
            <FoundationIconCalendar />
          </button>
          <input
            v-on="inputEvents"
            ref="nativeInput"
            :id="field.inputId || _name"
            autocomplete="off"
            class="form-date__native-input"
            :disabled="disabled"
            type="text"
            :value="inputValue"
            @blur="_setInputFocus(false)"
            @focus="_setInputFocus(true)"
          />
        </div>
      </template>
    </VDatePicker>
    <template #fallback>
      <input class="form-date__native-input" type="date" />
    </template>
  </ClientOnly>
</template>

<style scoped lang="scss">
.form-date {
  &__button {
    position: absolute;
    top: 50%;
    right: var(--form-input-padding-inline, 16px);
    width: 1em;
    height: 1em;
    margin: 0;
    padding: 0;
    font-size: var(--form-input-icon-size, 1.2rem);
    background: transparent;
    border: none;
    transform: translateY(-50%);
    cursor: pointer;

    svg {
      width: 1em;
      height: 1em;
    }
  }

  &__native-input {
    box-sizing: border-box;
    width: 100%;
    height: var(--form-input-height, 60px);
    font-size: var(--form-input-font-size, 1rem);
    line-height: var(--input-line-height, 1.5);
    border: var(--form-input-border-width, 1px) solid
      var(--form-input-border-color, #ddd);
    border-radius: var(--form-input-border-radius, 4px);
    padding-inline: var(--form-input-padding-inline, 16px);
    padding-block: var(--form-input-padding-block-start, 22px)
      var(--form-input-padding-block-end, 8px);

    &::-webkit-datetime-edit,
    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
}
</style>
